// package imports
import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { Session } from 'next-auth';

// local imports
import { MegaMenuSection, SiteHeaderType } from './SiteHeaderTypes';
import LogoBox from './DesktopMegaMenuComponents/LogoBox';
import NavigationToggleButton from './DesktopMegaMenuComponents/NavigationToggleButton';
import MegaMenuTransition from './DesktopMegaMenuComponents/MegaMenuTransition';
import MegaMenuNav from './DesktopMegaMenuComponents/MegaMenuNav';
import MegaMenuContainer from './DesktopMegaMenuComponents/MegaMenuContainer';
import { hasRealityCoachAccess, isCustomer } from 'services/subscriptionUtils';
import { LessonPlan } from 'types/AgilityTypes';
import { isMyAccountSection } from './SiteHeaderUtils';

const DesktopMegaMenu = ({
  headerFields,
  session,
  lessonPlanOfTheWeek,
}: {
  headerFields: SiteHeaderType;
  session: Session | null;
  lessonPlanOfTheWeek?: LessonPlan;
}) => {
  const headerRef = useRef<HTMLDivElement>(null);

  const shouldDisplaySection = (section: MegaMenuSection) => {
    if (section.fields.freeAccountDisplayOnly === 'true') {
      if (session?.user && !isCustomer(session?.user)) {
        return true;
      }
      return false;
    }
    if (section.fields.realityCoachPermissionRequired === 'true') {
      return hasRealityCoachAccess(session);
    }
    if (section.fields.realityCoachPermissionRequired === 'false') {
      return true;
    }
    if (section.fields.subscriptionRequiredForDisplay === 'true') {
      if (isCustomer(session?.user)) {
        return true;
      }
      return false;
    }
    if (section.fields.loggedInUserDisplayOnly === 'true') {
      if (session?.user) {
        return true;
      }
      return false;
    }
    if (section.fields.notLoggedInUserOnlyDisplay === 'true') {
      if (session?.user) {
        return false;
      }
      return true;
    }
    return true;
  };

  const myAccountSection = headerFields.fields.megaMenuSections.find(
    (section) => shouldDisplaySection(section) && isMyAccountSection(section),
  );

  return (
    <Box
      sx={{
        '@media (max-width: 1024px)': {
          display: 'none',
        },
      }}
      ref={headerRef}
    >
      <Box
        id="desktop-mega-menu"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '74px',
          maxWidth: '1392px',
          ml: 'auto',
          mr: 'auto',
          '@media (min-width: 1024px)': {
            pl: '1.5rem',
            pr: '1.5rem',
          },
          '@media (min-width: 1224px)': {
            pl: '2.5rem',
            pr: '2.5rem',
          },
        }}
      >
        <LogoBox megaMenuLogo={headerFields.fields.megaMenuLogo} />
        <nav className="flex-auto flex items-center lg:gap-6 xl:gap-10 max-md:hidden">
          {headerFields.fields.megaMenuSections.map((section) => {
            if (shouldDisplaySection(section) && !isMyAccountSection(section)) {
              return (
                <NavigationToggleButton
                  key={section.contentID}
                  section={section}
                  session={session}
                />
              );
            }
            return null;
          })}
        </nav>
        <div className="flex gap-3 flex-wrap flex-none md:ml-6 lg:ml-8 xl:ml-16">
          {myAccountSection && (
            <NavigationToggleButton
              section={myAccountSection}
              session={session}
            />
          )}
        </div>
      </Box>
      <MegaMenuContainer headerRef={headerRef}>
        <MegaMenuTransition>
          <MegaMenuNav
            headerFields={headerFields}
            session={session}
            lessonPlanOfTheWeek={lessonPlanOfTheWeek}
          />
        </MegaMenuTransition>
      </MegaMenuContainer>
    </Box>
  );
};

export default DesktopMegaMenu;
